import {
    SchedulerResourceModel as BryntumSchedulerResourceModel,
    DateHelper,
    DomClassList,
    Model,
} from "@bryntum/schedulerpro"
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro"
import { JobStatuses } from "@legacy/core/utils/enums"
import { useCallback } from "react"
import ReactDOMServer from "react-dom/server"

import useHasAccess from "@hooks/useHasAccess"

import formatTimelineViewCardLocationName from "@utils/formatTimelineViewCardLocationName"

import { Icon } from "@atoms/index"

import JobPreviewCard from "@pages/Jobs/JobList/views/JobTimelineView/JobPreviewCard/JobPreviewCard"
import styles from "@pages/Jobs/JobList/views/JobTimelineView/JobTimelineView.module.scss"
import {
    EventModel,
    JobTimelineScheduler,
    RenderData,
    ResourceTimeRangeModel,
    SchedulerEventModel,
    SchedulerResourceModel,
} from "@pages/Jobs/JobList/views/JobTimelineView/JobTimelineView.types"
import TimelineViewEventCard from "@pages/Jobs/JobList/views/JobTimelineView/TimelineViewEventCard/TimelineViewEventCard"
import TimelineViewTechniciansBarHeader from "@pages/Jobs/JobList/views/JobTimelineView/TimelineViewTechniciansBar/TimelineViewTechniciansBarHeader/TimelineViewTechniciansBarHeader"
import TimelineViewTechniciansBarTechCell from "@pages/Jobs/JobList/views/JobTimelineView/TimelineViewTechniciansBar/TimelineViewTechniciansBarTechCell/TimelineViewTechniciansBarTechCell"

import { PERMISSION_LEVEL } from "@constants/permissionLevel"

import useJobTimelineViewBryntumInstances from "./useJobTimelineViewBryntumInstances"
import useJobTimelineViewStates from "./useJobTimelineViewStates"

export default function useJobTimelineViewRenderers() {
    const { jobCardStyle, timelineViewOrientation, todayInUserTimezone, visibleDateRange } = useJobTimelineViewStates()
    const { schedulerPro, unscheduledJobsGrid } = useJobTimelineViewBryntumInstances()

    const { hasPermission } = useHasAccess()

    const renderUnscheduledJobsCellsTooltip = ({ record }: { record: EventModel<CalendarEvent> }) => {
        return (
            <JobPreviewCard
                jobId={record._data.id}
                isVisible={unscheduledJobsGrid.current?.instance.isPreviewModeEnabled}
                initialData={{
                    id: record._data.id,
                    custom_id: record._data.custom_id,
                    status: record._data.status,
                    status_label: record._data.status_label,
                    substatus_label: record._data.substatus_label,
                    status_color_override: record._data.status_color_override,
                    service_name: record._data.service_name,
                    client: record._data.client,
                    location: record._data.location,
                    city: record._data.city,
                    postal_code: record._data.postal_code,
                    start_time: String(record._data.start_time),
                    end_time: String(record._data.end_time),
                    duration: record._data.duration,
                    priority: record._data.priority,
                    assigned_technicians: record._data.assigned_technicians,
                }}
            />
        )
    }

    const renderUnscheduledJobsCells = ({ record }: { record: Model }) => {
        const jobEvent = record as unknown as CalendarEvent
        return ReactDOMServer.renderToString(
            <TimelineViewEventCard
                location={formatTimelineViewCardLocationName(jobEvent)}
                client={jobEvent.client}
                duration={jobEvent.duration}
                customId={jobEvent.custom_id}
                serviceName={jobEvent.service_name}
                isResizeEnabled={false}
                status={jobEvent.status}
                statusColorOverride={jobEvent.status_color_override}
                id={jobEvent.id}
                assignedTechnicians={[]}
                priority={jobEvent.priority}
            />,
        )
    }

    const renderTechniciansColumnCell = ({ record }: { record: Model }) => {
        const techRecord = record as SchedulerResourceModel<CalendarTechnician>

        const userId = techRecord?.originalData?.id

        if (userId === "unassigned") {
            return <TimelineViewTechniciansBarTechCell technician={null} />
        } else {
            return <TimelineViewTechniciansBarTechCell technician={techRecord?.originalData} />
        }
    }

    const renderTechniciansColumnHeader = () => {
        return ReactDOMServer.renderToStaticMarkup(<TimelineViewTechniciansBarHeader />)
    }

    const renderJobTimelineViewCard = useCallback(
        ({ eventRecord, renderData }: { eventRecord: SchedulerEventModel; renderData: RenderData }) => {
            const isDragAndResizeEnabled =
                eventRecord.originalData !== undefined &&
                eventRecord.originalData.status !== JobStatuses.cancelled &&
                eventRecord.originalData.status !== JobStatuses.finalized

            eventRecord.draggable = isDragAndResizeEnabled
            ;(renderData.wrapperCls as DomClassList).add("jsEventDroppableArea")

            const startDate = (eventRecord.resizingStartTime || eventRecord.startDate) as Date
            const endDate = (eventRecord.resizingEndTime || eventRecord.endDate) as Date

            const eventDuration = Math.abs((new Date(endDate).getTime() - new Date(startDate).getTime()) / 1000)

            return (
                eventRecord._data &&
                ReactDOMServer.renderToStaticMarkup(
                    <TimelineViewEventCard
                        startTime={startDate}
                        endTime={endDate}
                        status={eventRecord._data.status}
                        statusColorOverride={eventRecord._data.status_color_override}
                        customId={eventRecord._data.custom_id}
                        id={eventRecord._data.id}
                        assignedTechnicians={eventRecord._data.assigned_technicians}
                        serviceName={eventRecord._data.service_name}
                        client={eventRecord._data.client}
                        location={formatTimelineViewCardLocationName(eventRecord._data)}
                        isResizeEnabled={
                            hasPermission("jobs_edit_permission", PERMISSION_LEVEL.FULL) && isDragAndResizeEnabled
                        }
                        isCompact={jobCardStyle === "compact"}
                        isVertical={timelineViewOrientation === "vertical"}
                        duration={eventDuration}
                        isResizingEventStart={eventRecord.isResizingEventStart}
                        isResizingEventEnd={eventRecord.isResizingEventEnd}
                        isOverlappingOnResize={eventRecord.isOverlappingOnResize}
                        priority={eventRecord._data.priority}
                    />,
                )
            )
        },
        [jobCardStyle, timelineViewOrientation],
    )

    const renderJobTimelineViewCardTooltip = ({ eventRecord }: { eventRecord: SchedulerEventModel }) => {
        const jobDate = eventRecord.startDate as Date
        const isPastDue = DateHelper.isBefore(new Date(jobDate), new Date(todayInUserTimezone))

        if (eventRecord._data === undefined) {
            return null
        }

        // This is a hack to fix the status label when the event is being dragged.
        // The status label is not updated until the calendar endpoint that feeds the entire
        // timeline is fetched again, so we need to update the `Past Due` and `Upcoming`
        // labels manually.
        const statusLabel = ((statusLabel) => {
            if (statusLabel === "Past Due" && !isPastDue) {
                return "Upcoming"
            } else if (statusLabel === "Upcoming" && isPastDue) {
                return "Past Due"
            } else {
                return statusLabel
            }
        })(eventRecord._data.status_label)

        return (
            <JobPreviewCard
                jobId={eventRecord._data.id}
                initialData={{
                    id: eventRecord._data.id,
                    custom_id: eventRecord._data.custom_id,
                    status: eventRecord._data.status,
                    status_label: statusLabel,
                    substatus_label: eventRecord._data.substatus_label,
                    status_color_override: eventRecord._data.status_color_override,
                    service_name: eventRecord._data.service_name,
                    client: eventRecord._data.client,
                    location: eventRecord._data.location,
                    city: eventRecord._data.city,
                    postal_code: eventRecord._data.postal_code,
                    start_time: String(eventRecord._data.start_time),
                    end_time: String(eventRecord._data.end_time),
                    duration: eventRecord._data.duration,
                    priority: eventRecord._data.priority,
                    assigned_technicians: eventRecord._data.assigned_technicians,
                }}
            />
        )
    }

    const renderResourceTimeRange = ({
        resourceTimeRangeRecord,
    }: {
        resourceTimeRangeRecord: ResourceTimeRangeModel
    }) => {
        if (resourceTimeRangeRecord.data?.isOverlappingTimeRange) {
            return ReactDOMServer.renderToStaticMarkup(
                <div className={styles.overlappingRegionCounter}>
                    <Icon icon={icon({ name: "diamond-exclamation", style: "solid", family: "sharp" })} />
                    {resourceTimeRangeRecord.data.amountOfItems}
                </div>,
            )
        } else {
            return resourceTimeRangeRecord.name
        }
    }

    const renderUnscheduledJobsGridEmptyMessage = () => {
        return ReactDOMServer.renderToStaticMarkup(
            <div className={styles.unscheduledJobsEmptyState}>
                <Icon
                    className={styles.unscheduledJobsEmptyStateIcon}
                    icon={icon({ name: "circle-dashed", style: "solid", family: "sharp" })}
                    size={32}
                />

                <div className={styles.unscheduledJobsEmptyStateTextWrap}>
                    <div className={styles.unscheduledJobsEmptyStateTitle}>No Unscheduled Jobs</div>
                    <div className={styles.unscheduledJobsEmptyStateMessage}>
                        Once you unschedule a job, it&apos;ll appear here.
                    </div>
                </div>
            </div>,
        )
    }

    const renderVerticalTechnicianBarTechCell = useCallback(
        ({ resourceRecord }: { resourceRecord: BryntumSchedulerResourceModel }) => {
            const record = resourceRecord as SchedulerResourceModel<CalendarTechnician>
            const userId = record?.originalData?.id

            const currentDate = (schedulerPro.current?.instance as JobTimelineScheduler)?.currentVisibleDates?.start
            const timeFrameType = (schedulerPro.current?.instance as JobTimelineScheduler)?.timeFrameType

            if (userId === "unassigned") {
                return ReactDOMServer.renderToString(
                    <TimelineViewTechniciansBarTechCell
                        technician={null}
                        isCollapsed={false}
                        isVertical={true}
                        isCompact={false}
                    />,
                )
            } else {
                return ReactDOMServer.renderToString(
                    <TimelineViewTechniciansBarTechCell
                        technician={record?.originalData}
                        isCollapsed={false}
                        isVertical={true}
                        isCompact={false}
                        schedulerVisibleDate={currentDate}
                        schedulerTimeFrameType={timeFrameType}
                    />,
                )
            }
        },
        [visibleDateRange, schedulerPro],
    )

    return {
        renderResourceTimeRange,
        renderUnscheduledJobsCellsTooltip,
        renderUnscheduledJobsCells,
        renderTechniciansColumnCell,
        renderTechniciansColumnHeader,
        renderJobTimelineViewCard,
        renderJobTimelineViewCardTooltip,
        renderUnscheduledJobsGridEmptyMessage,
        renderVerticalTechnicianBarTechCell,
    }
}
